/**
 * Trees API functions
 */

import { authenticatedRequest } from "./client";

export interface TreeSummary {
  id: number;
  name: string;
  description: string | null;
  person_count: number;
  gedcom_filename: string | null;
  created_at: string;
  updated_at: string;
  user_can_edit?: boolean; // Whether current user can edit this tree
}

export interface PersonSummary {
  id: number;
  gedcom_id: string | null;
  given_name: string | null;
  surname: string | null;
  full_name: string | null;
  sex: string;
  birth_date: string | null;
  birth_date_text: string | null;
  birth_place_text: string | null;
  death_date: string | null;
  death_date_text: string | null;
  death_place_text: string | null;
  // Optional: added for global person selection across trees
  tree_id?: number;
  tree_name?: string;
}

export interface EventInfo {
  id: number;
  event_type: string;
  date_text: string | null;
  date_start: string | null;
  place_text: string | null;
  description: string | null;
  notes: string | null;
}

export interface FamilyMember {
  id: number;
  given_name: string | null;
  surname: string | null;
  sex: string;
  birth_date_text: string | null;
  death_date_text: string | null;
}

export interface PersonDetail extends PersonSummary {
  notes: string | null;
  events: EventInfo[];
  parents: FamilyMember[];
  siblings: FamilyMember[];
  spouses: FamilyMember[];
  children: FamilyMember[];
  created_at: string;
  updated_at: string;
}

export interface ImportPreview {
  filename: string;
  tree_name: string | null;
  person_count: number;
  family_count: number;
  source_count: number;
  sample_persons: Array<{
    gedcom_id: string;
    name: string;
    birth: string | null;
    death: string | null;
  }>;
}

export interface ImportResult {
  tree_id: number;
  tree_name: string;
  persons_imported: number;
  families_imported: number;
  sources_imported: number;
  warnings: string[];
}

export interface TreeMember {
  user_id: string;
  user_email: string;
  user_name: string | null;
  role: "TREE_ADMIN" | "TREE_VIEWER";
  created_at: string;
}

export interface AddTreeMemberRequest {
  user_id: string;
  role: "TREE_ADMIN" | "TREE_VIEWER";
}

export interface UpdateTreeMemberRequest {
  role: "TREE_ADMIN" | "TREE_VIEWER";
}

export interface PersonDiff {
  gedcom_id: string;
  name: string;
  birth?: string;
  death?: string;
  changes?: string[];
}

export interface FamilyDiff {
  gedcom_id: string;
  husband?: string;
  wife?: string;
  spouses?: string;
  children?: string;
  marriage?: string;
  changes?: string[];
}

export interface MergePreview {
  persons_to_add: PersonDiff[];
  persons_to_update: PersonDiff[];
  families_to_add: FamilyDiff[];
  families_to_update: FamilyDiff[];
  summary: {
    total_additions: number;
    total_updates: number;
  };
}

// Backend response format (different from frontend MergePreview)
interface BackendPersonChange {
  person_id: number | null;
  gedcom_id: string;
  change_type: string;
  full_name: string;
  birth_date: string | null;
  birth_place: string | null;
  death_date: string | null;
  field_changes: Array<{
    field_name: string;
    old_value: string | null;
    new_value: string | null;
  }>;
}

interface BackendFamilyChange {
  family_unit_id: number | null;
  gedcom_id: string;
  change_type: string;
  spouse_count: number;
  child_count: number;
  marriage_date: string | null;
  marriage_place: string | null;
}

interface MergePreviewResponse {
  person_changes: BackendPersonChange[];
  family_changes: BackendFamilyChange[];
  summary: {
    new_persons: number;
    updated_persons: number;
    new_families: number;
    updated_families: number;
  };
}

export interface MergeResult {
  tree_id: number;
  tree_name: string;
  persons_added: number;
  persons_updated: number;
  families_added: number;
  families_updated: number;
}

/**
 * List trees (optionally filtered by vault)
 */
export async function listTrees(token: string, vaultId?: number): Promise<TreeSummary[]> {
  const params = vaultId ? new URLSearchParams({ vault_id: String(vaultId) }) : undefined;
  return authenticatedRequest<TreeSummary[]>(
    { method: "GET", url: "/trees", params },
    token
  );
}

/**
 * Get tree details
 */
export async function getTree(
  treeId: number,
  token: string
): Promise<TreeSummary> {
  return authenticatedRequest<TreeSummary>(
    { method: "GET", url: `/trees/${treeId}` },
    token
  );
}

export interface PersonFilters {
  limit?: number;
  offset?: number;
  search?: string;
  sex?: string;
  birthYearMin?: number;
  birthYearMax?: number;
}

/**
 * List persons in a tree
 */
export async function listPersons(
  treeId: number,
  token: string,
  options?: PersonFilters
): Promise<PersonSummary[]> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.offset) params.set("offset", String(options.offset));
  if (options?.search) params.set("search", options.search);
  if (options?.sex) params.set("sex", options.sex);
  if (options?.birthYearMin) params.set("birth_year_min", String(options.birthYearMin));
  if (options?.birthYearMax) params.set("birth_year_max", String(options.birthYearMax));

  return authenticatedRequest<PersonSummary[]>(
    {
      method: "GET",
      url: `/trees/${treeId}/persons`,
      params,
    },
    token
  );
}

/**
 * Get count of persons matching filters
 */
export async function countPersons(
  treeId: number,
  token: string,
  options?: { search?: string; sex?: string }
): Promise<number> {
  const params = new URLSearchParams();
  if (options?.search) params.set("search", options.search);
  if (options?.sex) params.set("sex", options.sex);

  const result = await authenticatedRequest<{ count: number }>(
    {
      method: "GET",
      url: `/trees/${treeId}/persons/count`,
      params,
    },
    token
  );
  return result.count;
}

/**
 * Get person details
 */
export async function getPerson(
  treeId: number,
  personId: number,
  token: string
): Promise<PersonDetail> {
  return authenticatedRequest<PersonDetail>(
    { method: "GET", url: `/trees/${treeId}/persons/${personId}` },
    token
  );
}

/**
 * Preview GEDCOM import
 */
export async function previewImport(
  file: File,
  token: string
): Promise<ImportPreview> {
  const formData = new FormData();
  formData.append("file", file);

  return authenticatedRequest<ImportPreview>(
    {
      method: "POST",
      url: "/trees/import/preview",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    },
    token
  );
}

/**
 * Import GEDCOM file as new tree
 */
export async function importGedcom(
  file: File,
  token: string,
  treeName?: string,
  vaultId?: number
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  if (treeName) {
    formData.append("tree_name", treeName);
  }
  if (vaultId) {
    formData.append("vault_id", vaultId.toString());
  }

  return authenticatedRequest<ImportResult>(
    {
      method: "POST",
      url: "/trees/import",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    },
    token
  );
}

/**
 * Delete a tree
 */
export async function deleteTree(
  treeId: number,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    { method: "DELETE", url: `/trees/${treeId}` },
    token
  );
}

/**
 * Person update data
 */
export interface PersonUpdate {
  given_name?: string | null;
  surname?: string | null;
  full_name?: string | null;
  sex?: string | null;
  birth_date_text?: string | null;
  birth_place_text?: string | null;
  death_date_text?: string | null;
  death_place_text?: string | null;
  notes?: string | null;
}

/**
 * Update a person
 */
export async function updatePerson(
  treeId: number,
  personId: number,
  data: PersonUpdate,
  token: string
): Promise<PersonSummary> {
  return authenticatedRequest<PersonSummary>(
    {
      method: "PUT",
      url: `/trees/${treeId}/persons/${personId}`,
      data,
    },
    token
  );
}

/**
 * Delete a person from the tree
 */
export async function deletePerson(
  treeId: number,
  personId: number,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    {
      method: "DELETE",
      url: `/trees/${treeId}/persons/${personId}`,
    },
    token
  );
}

/**
 * Event create data
 */
export interface EventCreate {
  event_type: string;
  date_text?: string | null;
  place_text?: string | null;
  description?: string | null;
  notes?: string | null;
}

/**
 * Event update data
 */
export interface EventUpdate {
  event_type?: string | null;
  date_text?: string | null;
  place_text?: string | null;
  description?: string | null;
  notes?: string | null;
}

/**
 * Create an event for a person
 */
export async function createEvent(
  treeId: number,
  personId: number,
  data: EventCreate,
  token: string
): Promise<EventInfo> {
  return authenticatedRequest<EventInfo>(
    {
      method: "POST",
      url: `/trees/${treeId}/persons/${personId}/events`,
      data,
    },
    token
  );
}

/**
 * Update an event
 */
export async function updateEvent(
  treeId: number,
  eventId: number,
  data: EventUpdate,
  token: string
): Promise<EventInfo> {
  return authenticatedRequest<EventInfo>(
    {
      method: "PUT",
      url: `/trees/${treeId}/events/${eventId}`,
      data,
    },
    token
  );
}

/**
 * Delete an event
 */
export async function deleteEvent(
  treeId: number,
  eventId: number,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    {
      method: "DELETE",
      url: `/trees/${treeId}/events/${eventId}`,
    },
    token
  );
}

/**
 * Export tree as GEDCOM file
 * Returns the GEDCOM content as a string for download
 */
export async function exportTree(
  treeId: number,
  token: string
): Promise<{ content: string; filename: string }> {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  const response = await fetch(`${API_URL}/trees/${treeId}/export`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to export tree");
  }

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = "tree.ged";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match) {
      filename = match[1];
    }
  }

  const content = await response.text();
  return { content, filename };
}

// Media types
export interface MediaItem {
  id: number;
  tree_id: number;
  person_id: number | null;
  event_id: number | null;
  filename: string;
  original_filename: string;
  content_type: string;
  file_size: number;
  media_type: string;
  caption: string | null;
  is_profile: boolean;
  date_taken: string | null;
  created_at: string;
}

/**
 * Upload a media file
 */
export async function uploadMedia(
  file: File,
  treeId: number,
  token: string,
  options?: {
    vaultId?: number; // Required for authorization
    personId?: number;
    eventId?: number;
    caption?: string;
    isProfile?: boolean;
    mediaTypeOverride?: string; // Override auto-detected type: photo, document, audio, video, other
    isVisibleToAll?: boolean; // Visible to all vault members (default: true)
    isPrivate?: boolean; // Private to uploader and vault admins (default: false)
  }
): Promise<MediaItem> {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  const formData = new FormData();
  formData.append("file", file);
  
  // Vault ID is required for authorization
  if (options?.vaultId) {
    formData.append("vault_id", String(options.vaultId));
  }
  
  // Only append tree_id if valid
  if (treeId && treeId > 0) {
    formData.append("tree_id", String(treeId));
  }
  if (options?.personId) formData.append("person_id", String(options.personId));
  if (options?.eventId) formData.append("event_id", String(options.eventId));
  if (options?.caption) formData.append("caption", options.caption);
  if (options?.isProfile) formData.append("is_profile", "true");
  if (options?.mediaTypeOverride) formData.append("media_type_override", options.mediaTypeOverride);
  
  // Visibility options
  if (options?.isVisibleToAll !== undefined) {
    formData.append("is_visible_to_all_vault_members", String(options.isVisibleToAll));
  }
  if (options?.isPrivate !== undefined) {
    formData.append("is_private_to_uploader_and_vault_admins", String(options.isPrivate));
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}/media/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  } catch (networkError) {
    // Network error (CORS, connection refused, etc.)
    console.error("Media upload network error:", networkError);
    throw new Error(
      `Network error: Unable to connect to server. Please check your connection and try again.`
    );
  }

  if (!response.ok) {
    let errorMessage = `Upload failed (${response.status})`;
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch {
      // Response wasn't JSON, try to get text
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // Ignore
      }
    }
    console.error("Media upload failed:", response.status, errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Get media URL for display
 */
export function getMediaUrl(mediaId: number): string {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  return `${API_URL}/media/${mediaId}`;
}

/**
 * List media for a person
 */
export async function listPersonMedia(
  personId: number,
  token: string
): Promise<MediaItem[]> {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  const response = await fetch(`${API_URL}/media/person/${personId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch media");
  }

  return response.json();
}

/**
 * Delete a media file
 */
export async function deleteMedia(mediaId: number, token: string): Promise<void> {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  const response = await fetch(`${API_URL}/media/${mediaId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete media");
  }
}

/**
 * Update media metadata
 */
export async function updateMedia(
  mediaId: number,
  data: { caption?: string; is_profile?: boolean; date_taken?: string },
  token: string
): Promise<MediaItem> {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
  const response = await fetch(`${API_URL}/media/${mediaId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update media");
  }

  return response.json();
}

/**
 * Format person display name
 */
export function getDisplayName(person: PersonSummary | FamilyMember): string {
  if ("full_name" in person && person.full_name) {
    return person.full_name;
  }
  const parts: string[] = [];
  if (person.given_name) parts.push(person.given_name);
  if (person.surname) parts.push(person.surname);
  return parts.join(" ") || "Unknown";
}

/**
 * Format life span for display
 */
export function getLifeSpan(person: PersonSummary): string {
  const birth = person.birth_date_text;
  const death = person.death_date_text;

  if (!birth && !death) return "";
  if (birth && !death) return `b. ${birth}`;
  if (!birth && death) return `d. ${death}`;
  return `${birth} – ${death}`;
}

/**
 * Event type display labels
 */
export const EVENT_TYPE_LABELS: Record<string, string> = {
  BIRT: "Birth",
  DEAT: "Death",
  BURI: "Burial",
  BAPM: "Baptism",
  CHR: "Christening",
  MARR: "Marriage",
  DIV: "Divorce",
  RESI: "Residence",
  CENS: "Census",
  OCCU: "Occupation",
  IMMI: "Immigration",
  EMIG: "Emigration",
  NATU: "Naturalization",
  GRAD: "Graduation",
  RETI: "Retirement",
  WILL: "Will",
  PROB: "Probate",
  EVEN: "Event",
};

// Tree visualization types
export type ViewType = "pedigree" | "descendants" | "hourglass";

export interface TreeNodeRelation {
  id: string;
  type: "blood" | "adopted" | "half" | "married" | "divorced";
}

export interface TreeNodeData {
  name: string;
  birthDate: string | null;
  deathDate: string | null;
  birthPlace: string | null;
  sex: string;
}

export interface TreeNode {
  id: string;
  gender: "male" | "female";
  parents: TreeNodeRelation[];
  children: TreeNodeRelation[];
  siblings: TreeNodeRelation[];
  spouses: TreeNodeRelation[];
  data: TreeNodeData;
}

export interface VisualizationData {
  nodes: TreeNode[];
  rootId: string;
}

/**
 * Get tree visualization data
 */
export async function getVisualizationData(
  treeId: number,
  rootId: number,
  viewType: ViewType,
  token: string,
  generations: number = 4
): Promise<VisualizationData> {
  return authenticatedRequest<VisualizationData>(
    {
      method: "GET",
      url: `/trees/${treeId}/visualization`,
      params: new URLSearchParams({
        root_id: String(rootId),
        view_type: viewType,
        generations: String(generations),
      }),
    },
    token
  );
}

// Hierarchy types for D3 tree visualization
export interface HierarchyNode {
  id: number;
  name: string;
  givenName: string | null;
  surname: string | null;
  sex: "M" | "F" | "U";
  birthDate: string | null;
  deathDate: string | null;
  birthPlace: string | null;
  father?: HierarchyNode;
  mother?: HierarchyNode;
  children?: HierarchyNode[];
}

export interface HierarchyResponse {
  hierarchy: HierarchyNode;
  rootId: number;
  direction: "ancestors" | "descendants";
  treeName: string;
}/**
 * Get hierarchical tree data for D3 visualization
 */
export async function getHierarchyData(
  treeId: number,
  token: string,
  options?: {
    rootId?: number;
    direction?: "ancestors" | "descendants";
    maxGenerations?: number;
  }
): Promise<HierarchyResponse> {
  const params = new URLSearchParams();
  if (options?.rootId) params.set("root_id", String(options.rootId));
  if (options?.direction) params.set("direction", options.direction);
  if (options?.maxGenerations) params.set("max_generations", String(options.maxGenerations));

  return authenticatedRequest<HierarchyResponse>(
    {
      method: "GET",
      url: `/trees/${treeId}/hierarchy`,
      params,
    },
    token
  );
}

/**
 * List tree members
 */
export async function listTreeMembers(
  treeId: number,
  token: string
): Promise<TreeMember[]> {
  return authenticatedRequest<TreeMember[]>(
    { method: "GET", url: `/trees/${treeId}/members` },
    token
  );
}

/**
 * Add a member to a tree
 */
export async function addTreeMember(
  treeId: number,
  data: AddTreeMemberRequest,
  token: string
): Promise<TreeMember> {
  return authenticatedRequest<TreeMember>(
    {
      method: "POST",
      url: `/trees/${treeId}/members`,
      data,
    },
    token
  );
}

/**
 * Update a tree member's role
 */
export async function updateTreeMember(
  treeId: number,
  userId: string,
  data: UpdateTreeMemberRequest,
  token: string
): Promise<TreeMember> {
  return authenticatedRequest<TreeMember>(
    {
      method: "PUT",
      url: `/trees/${treeId}/members/${userId}`,
      data,
    },
    token
  );
}

/**
 * Remove a member from a tree
 */
export async function removeTreeMember(
  treeId: number,
  userId: string,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    {
      method: "DELETE",
      url: `/trees/${treeId}/members/${userId}`,
    },
    token
  );
}

/**
 * Preview GEDCOM merge into existing tree
 */
export async function previewMerge(
  treeId: number,
  file: File,
  token: string
): Promise<MergePreview> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await authenticatedRequest<MergePreviewResponse>(
    {
      method: "POST",
      url: `/trees/${treeId}/merge/preview`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    token
  );

  // Transform backend response to frontend format
  const transformPerson = (p: BackendPersonChange): PersonDiff => ({
    gedcom_id: p.gedcom_id,
    name: p.full_name,
    birth: p.birth_date || undefined,
    death: p.death_date || undefined,
    changes: p.field_changes?.map((fc) => `${fc.field_name}: ${fc.old_value} → ${fc.new_value}`) || [],
  });

  const transformFamily = (f: BackendFamilyChange): FamilyDiff => ({
    gedcom_id: f.gedcom_id,
    spouses: `${f.spouse_count} spouse(s)`,
    children: `${f.child_count} child(ren)`,
    marriage: f.marriage_date ? `Married ${f.marriage_date}` : undefined,
  });

  return {
    persons_to_add: response.person_changes?.filter((p) => p.change_type === "new").map(transformPerson) || [],
    persons_to_update: response.person_changes?.filter((p) => p.change_type === "update").map(transformPerson) || [],
    families_to_add: response.family_changes?.filter((f) => f.change_type === "new").map(transformFamily) || [],
    families_to_update: response.family_changes?.filter((f) => f.change_type === "update").map(transformFamily) || [],
    summary: {
      total_additions: (response.summary?.new_persons || 0) + (response.summary?.new_families || 0),
      total_updates: (response.summary?.updated_persons || 0) + (response.summary?.updated_families || 0),
    },
  };
}

/**
 * Apply GEDCOM merge to existing tree
 */
export async function applyMerge(
  treeId: number,
  file: File,
  acceptedIds: {
    person_ids: string[];
    family_ids: string[];
  },
  token: string
): Promise<MergeResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("accepted_person_ids", acceptedIds.person_ids.join(","));
  formData.append("accepted_family_ids", acceptedIds.family_ids.join(","));

  return authenticatedRequest<MergeResult>(
    {
      method: "POST",
      url: `/trees/${treeId}/merge/apply`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    token
  );
}
